export const RESULT_CLASSES = ["High Master", "Master", "Expert", "Sharpshooter", "Marksman"];
export const separateClassGroups = () => RESULT_CLASSES.map((name, index) => ({ id: `class-${index}`, title: name, classes: [name] }));
export const defaultResultSettings = () => ({ schemaVersion: 1, standardGroups: separateClassGroups(),
  twentyTwoGroups: separateClassGroups(), minimumEntries: null, programNotes: "", classAwardExclusion: "match_winner", classAwardThresholds: null,
  showExcludedClassWinners: false, tieBreakFiringOrder: [] });

export function classAwardThresholdErrors(thresholds) {
  if (thresholds == null) return [];
  if (typeof thresholds !== "object" || Array.isArray(thresholds)) return ["Enter class-award thresholds."];
  const errors = [];
  for (const [key, label] of [["second", "Second"], ["third", "Third"]]) {
    if (!Number.isInteger(thresholds[key]) || thresholds[key] < 2 || thresholds[key] > 1000) {
      errors.push(`${label} award minimum must be a whole number from 2 to 1000.`);
    }
  }
  if (!errors.length && thresholds.third < thresholds.second) errors.push("Third award minimum cannot be less than Second.");
  return errors;
}

export function classAwardCount(entrantCount, thresholds, eligible = true) {
  if (!eligible || entrantCount <= 0) return 0;
  if (thresholds == null) return Math.min(3, entrantCount);
  // Invalid or incomplete policies must not invent additional class awards.
  if (classAwardThresholdErrors(thresholds).length) return 1;
  return Math.min(entrantCount, 1 + Number(entrantCount >= thresholds.second) + Number(entrantCount >= thresholds.third));
}

export function classAwardThresholdDescription(thresholds) {
  return thresholds == null ? "Up to three awards (no entrant thresholds)"
    : `Second: ${thresholds.second} entrants; Third: ${thresholds.third} entrants`;
}

export function normalizeResultClass(value) {
  const label = String(value || "").trim().toLowerCase();
  if (!label || ["unc", "u"].includes(label) || label.includes("unclassified")) return "Unclassified";
  if (label.includes("high") && label.includes("master")) return "High Master";
  if (label.includes("sharpshooter")) return "Sharpshooter";
  if (label.includes("marksman")) return "Marksman";
  if (label.includes("master")) return "Master";
  if (label.includes("expert")) return "Expert";
  return String(value).trim();
}
export const competingResultClass = value => normalizeResultClass(value) === "Unclassified" ? "Master" : normalizeResultClass(value);
export const registeredProgram = match => /registered|state|regional|sectional|national/i.test(String(match?.sanctionedType || ""));
export function isTwentyTwoEntrant(row) {
  const categories = row?.categories || row?.categoryIds || row?.category || [];
  return (Array.isArray(categories) ? categories : String(categories).split(","))
    .some(value => /^(?:twenty.two.only.aggregate|\.?22[ _](?:calib(?:er|re)[ _])?only(?:[ _]aggregate)?)$/i.test(String(value).trim()));
}

export function rosterGroupingCounts(participants, entries = []) {
  const result = { standard: {}, twentyTwo: {}, unrecognized: [] };
  for (const participant of participants) {
    if (!["active", "confirmed", ""].includes(String(participant.status || "").toLowerCase())) continue;
    const activeEntries = entries.filter(entry => entry.participantId === participant.participantId && (!entry.status || entry.status === "active"));
    const rimfire = isTwentyTwoEntrant(participant) || activeEntries.some(entry => isTwentyTwoEntrant(entry)
      || isTwentyTwoEntrant(entry.metadata));
    const scope = rimfire ? "twentyTwo" : "standard";
    const cls = competingResultClass(participant.classification);
    if (!RESULT_CLASSES.includes(cls)) result.unrecognized.push(participant.displayName || cls);
    result[scope][cls] = (result[scope][cls] || 0) + 1;
  }
  return result;
}

export function combineClassGroups(groups, selected) {
  const chosen = groups.filter(group => selected.includes(group.id));
  if (chosen.length < 2) return groups;
  const classes = RESULT_CLASSES.filter(cls => chosen.some(group => group.classes.includes(cls)));
  const merged = { id: chosen[0].id, title: classes.join(" / "), classes };
  return [...groups.filter(group => !selected.includes(group.id)), merged]
    .sort((a, b) => RESULT_CLASSES.indexOf(a.classes[0]) - RESULT_CLASSES.indexOf(b.classes[0]));
}

export function suggestClassGroups(counts, minimum, { highMasterOpenOnly = false } = {}) {
  let groups = separateClassGroups();
  // Suggest upward combinations from the lowest class, never automatically into High Master.
  for (let i = groups.length - 1; i > 1; i -= 1) {
    const count = groups[i].classes.reduce((sum, cls) => sum + (counts[cls] || 0), 0);
    if (count > 0 && count < minimum) groups = combineClassGroups(groups, [groups[i].id, groups[i - 1].id]);
  }
  if (highMasterOpenOnly && (counts["High Master"] || 0) < minimum) {
    groups[0] = { ...groups[0], classAwardsEligible: false };
  }
  return groups;
}

export const requiresGroupingNotes = settings => [...settings.standardGroups, ...settings.twentyTwoGroups]
  .some(group => group.classes.length > 1 || group.classAwardsEligible === false);

export function groupingErrors(settings, match) {
  const errors = classAwardThresholdErrors(settings.classAwardThresholds);
  for (const [key, name] of [["standardGroups", "Standard"], ["twentyTwoGroups", ".22 Only"]]) {
    const groups = settings[key] || [];
    const classes = groups.flatMap(group => group.classes || []);
    if (groups.length < 1 || groups.length > 5 || classes.length !== 5 || RESULT_CLASSES.some(cls => classes.filter(value => value === cls).length !== 1)) {
      errors.push(`${name}: assign every class exactly once.`);
    }
    if (groups.some(group => !group.title.trim() || group.title.trim().length > 80)) errors.push(`${name}: group names must be 1-80 characters.`);
    if (new Set(groups.map(group => group.title.trim().toLowerCase())).size !== groups.length) errors.push(`${name}: use distinct group names.`);
    if (groups.some(group => group.classAwardsEligible === false && (group.classes.length !== 1 || group.classes[0] !== "High Master"))) {
      errors.push(`${name}: open-awards-only applies to a separate High Master group.`);
    }
  }
  if (settings.minimumEntries !== null && (!Number.isInteger(settings.minimumEntries) || settings.minimumEntries < 2 || settings.minimumEntries > 1000)) errors.push("Minimum entries must be 2-1000 or blank.");
  if (requiresGroupingNotes(settings) && !settings.programNotes.trim()) errors.push("Record the published program's class-award policy in Program notes.");
  if (registeredProgram(match) && settings.classAwardExclusion === "none") errors.push("Registered championship programs must exclude the match winner from class awards.");
  return errors;
}
